/**
 * Plan-based feature permissions utility
 * Centralized system for managing what features are available to different subscription plans
 */

// Available subscription plans
export const SUBSCRIPTION_PLANS = {
  SOLO: 'solo',
  PRO: 'pro', 
  MAX: 'max',
  NONE: 'none'
};

const PAID_PLANS = [SUBSCRIPTION_PLANS.SOLO, SUBSCRIPTION_PLANS.PRO, SUBSCRIPTION_PLANS.MAX];

const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due', 'canceling'];

// Orgs granted full Max-tier access without Stripe billing (must match backend subscriptionUtils.js).
const COMPLIMENTARY_MAX_ACCESS_COMPANY_IDS = new Set([
  '6975470d82d46988d9bd53db', // Greenberg Traurig
]);

function getUserCompanyId(user) {
  if (!user) return null;
  if (user.companyId) return String(user.companyId);
  if (user.company?.id) return String(user.company.id);
  if (user.company?._id) return String(user.company._id);
  return null;
}

function hasComplimentaryMaxAccess(user) {
  const companyId = getUserCompanyId(user);
  return companyId != null && COMPLIMENTARY_MAX_ACCESS_COMPANY_IDS.has(companyId);
}

/**
 * Check whether the org has a currently billable subscription.
 */
export const hasActiveSubscription = (user) => {
  if (!user) return false;

  if (hasComplimentaryMaxAccess(user)) {
    return true;
  }

  const plan = user.company?.subscriptionPlan || user.subscriptionPlan;
  const status = user.company?.subscriptionStatus || user.subscriptionStatus;

  if (!plan || !PAID_PLANS.includes(plan)) {
    return false;
  }

  if (!status || status === 'none') {
    return false;
  }

  return ACTIVE_SUBSCRIPTION_STATUSES.includes(status);
};

// Feature definitions
export const FEATURES = {
  PROJECTS: 'projects',
  ORGANIZATION: 'organization',
  TEAM_BILLING: 'team_billing',
  CUSTOM_DOMAIN: 'custom_domain',
  CUSTOM_BRANDING: 'custom_branding',
  INTEGRATIONS: 'integrations'
};

// Plan feature permissions mapping
const PLAN_PERMISSIONS = {
  [SUBSCRIPTION_PLANS.SOLO]: {
    [FEATURES.PROJECTS]: false,
    [FEATURES.ORGANIZATION]: false,
    [FEATURES.TEAM_BILLING]: false,
    [FEATURES.CUSTOM_DOMAIN]: false,
    [FEATURES.CUSTOM_BRANDING]: false,
    [FEATURES.INTEGRATIONS]: false
  },
  [SUBSCRIPTION_PLANS.PRO]: {
    [FEATURES.PROJECTS]: true,
    [FEATURES.ORGANIZATION]: true,
    [FEATURES.TEAM_BILLING]: true,
    [FEATURES.CUSTOM_DOMAIN]: true,
    [FEATURES.CUSTOM_BRANDING]: true,
    [FEATURES.INTEGRATIONS]: true
  },
  [SUBSCRIPTION_PLANS.MAX]: {
    [FEATURES.PROJECTS]: true,
    [FEATURES.ORGANIZATION]: true,
    [FEATURES.TEAM_BILLING]: true,
    [FEATURES.CUSTOM_DOMAIN]: true,
    [FEATURES.CUSTOM_BRANDING]: true,
    [FEATURES.INTEGRATIONS]: true
  },
  [SUBSCRIPTION_PLANS.NONE]: {
    [FEATURES.PROJECTS]: false,
    [FEATURES.ORGANIZATION]: false,
    [FEATURES.TEAM_BILLING]: false,
    [FEATURES.CUSTOM_DOMAIN]: false,
    [FEATURES.CUSTOM_BRANDING]: false,
    [FEATURES.INTEGRATIONS]: false
  }
};

/**
 * Get the user's subscription plan from user object
 * @param {Object} user - User object
 * @returns {string} - Subscription plan
 */
export const getUserPlan = (user) => {
  if (!user) return SUBSCRIPTION_PLANS.NONE;

  if (hasComplimentaryMaxAccess(user)) {
    return SUBSCRIPTION_PLANS.MAX;
  }

  if (!hasActiveSubscription(user)) {
    return SUBSCRIPTION_PLANS.NONE;
  }
  
  // Check if user has company with subscription plan
  if (user.company?.subscriptionPlan) {
    return user.company.subscriptionPlan;
  }
  
  // Fallback to user-level subscription plan if exists
  if (user.subscriptionPlan) {
    return user.subscriptionPlan;
  }
  
  return SUBSCRIPTION_PLANS.NONE;
};

/**
 * Get user's role from localStorage or default to member
 * @returns {string} - User role ('owner', 'admin', 'member')
 */
export const getUserRole = () => {
  try {
    return localStorage.getItem('userRole') || 'member';
  } catch (error) {
    return 'member';
  }
};

/**
 * Check if user has organization admin privileges
 * @param {string} userRole - User role (optional, will get from localStorage if not provided)
 * @returns {boolean} - Whether user is admin or owner
 */
export const isOrganizationAdmin = (userRole = null) => {
  const role = userRole || getUserRole();
  return role === 'admin' || role === 'owner';
};

/**
 * Check if a user has access to a specific feature
 * @param {Object} user - User object
 * @param {string} feature - Feature to check (use FEATURES constants)
 * @returns {boolean} - Whether user has access to the feature
 */
export const hasFeatureAccess = (user, feature) => {
  const userPlan = getUserPlan(user);
  
  // Get permissions for the user's plan
  const planPermissions = PLAN_PERMISSIONS[userPlan] || PLAN_PERMISSIONS[SUBSCRIPTION_PLANS.NONE];
  
  return planPermissions[feature] || false;
};

/**
 * Check if user has access to projects feature
 * @param {Object} user - User object
 * @returns {boolean} - Whether user can access projects
 */
export const canAccessProjects = (user) => {
  return hasFeatureAccess(user, FEATURES.PROJECTS);
};

/**
 * Check if user has access to organization features (basic plan check only)
 * @param {Object} user - User object
 * @returns {boolean} - Whether user can access organization features
 */
export const canAccessOrganization = (user) => {
  return hasFeatureAccess(user, FEATURES.ORGANIZATION);
};

/**
 * Check if user can manage organization settings (plan + admin role check)
 * @param {Object} user - User object
 * @param {string} userRole - User role (optional, will get from localStorage if not provided)
 * @returns {boolean} - Whether user can manage organization settings
 */
export const canManageOrganization = (user, userRole = null) => {
  // First check if plan supports organization features
  if (!hasFeatureAccess(user, FEATURES.ORGANIZATION)) {
    return false;
  }
  
  // Then check if user has admin privileges
  return isOrganizationAdmin(userRole);
};

/**
 * Check if user can access organization settings pages (includes read-only access for members)
 * @param {Object} user - User object
 * @returns {boolean} - Whether user can view organization pages
 */
export const canAccessOrganizationPages = (user) => {
  return hasFeatureAccess(user, FEATURES.ORGANIZATION);
};

/**
 * Check if user has access to custom domain features
 * @param {Object} user - User object
 * @returns {boolean} - Whether user can access custom domain features
 */
export const canAccessCustomDomain = (user) => {
  return hasFeatureAccess(user, FEATURES.CUSTOM_DOMAIN);
};

/**
 * Check if user has access to custom branding features
 * @param {Object} user - User object
 * @returns {boolean} - Whether user can access custom branding features
 */
export const canAccessCustomBranding = (user) => {
  return hasFeatureAccess(user, FEATURES.CUSTOM_BRANDING);
};

/**
 * Get all features available to a user's plan
 * @param {Object} user - User object
 * @returns {Object} - Object with feature names as keys and boolean access as values
 */
export const getUserFeatures = (user) => {
  const userPlan = getUserPlan(user);
  return PLAN_PERMISSIONS[userPlan] || PLAN_PERMISSIONS[SUBSCRIPTION_PLANS.NONE];
};

/**
 * Check if a plan is considered a team plan (has organization features)
 * @param {string} plan - Plan name
 * @returns {boolean} - Whether plan supports team features
 */
export const isTeamPlan = (plan) => {
  return plan === SUBSCRIPTION_PLANS.PRO || plan === SUBSCRIPTION_PLANS.MAX;
};

/**
 * Get plan display name
 * @param {string} plan - Plan name
 * @returns {string} - Display name for the plan
 */
export const getPlanDisplayName = (plan) => {
  const displayNames = {
    [SUBSCRIPTION_PLANS.SOLO]: 'Solo',
    [SUBSCRIPTION_PLANS.PRO]: 'Pro',
    [SUBSCRIPTION_PLANS.MAX]: 'Max',
    [SUBSCRIPTION_PLANS.NONE]: 'No Plan'
  };
  
  return displayNames[plan] || 'Unknown Plan';
}; 